'use strict'

// Bare shared-library import, not a deep workspacePrefix.js subpath — see
// app.js's identical fix note (tickets/catalog-fixall.md P0).
import { shellStripWorkspacePrefix } from '../functions/shellSharedDelegates.js'

// DemoClaimBanner — in-product conversion for demo sandbox sessions.
//
// When the signed-in user is a demo account (`root.isDemo` — seeded from
// getMe()'s `isDemo` flag by bootShell._resolveAuthSnapshot), a slim
// persistent bar pins to the bottom-left inviting them to claim/keep the
// workspace. Clicking "Claim this workspace" opens the brand Modal whose body
// is the SAME sign-in window the /login page renders (LoginWindow from
// @symbo-ls/workspace-shared) — REUSED, not rebuilt — supporting BOTH paths
// the /login chrome already ships:
//
//   • SIGN UP (new account): slot `SignupForm`. Submit calls
//     `el.sdk.claimDemoAccount({ email, password, name })` — the server
//     upgrades the current demo account IN PLACE (sets the password + real
//     email, clears `isDemo` on the user and their org so the sandbox stops
//     resetting). The workspace is KEPT. The demo tokens stay valid (same
//     account row), so we hand them off straight to my.symbols.app.
//
//     PENDING EMAIL (server core/auth-takeover-paths B-5): the server no
//     longer switches the account to the new email at claim time. It parks
//     the address on `pendingEmail`, mails a verification link to it, and
//     answers `{ user, pendingEmail, emailVerificationSent }`. The window then
//     swaps the forms for a PENDING panel (`root._demoClaimPendingEmail`): the
//     new email stays pending until the link is clicked, and sign-in with it
//     does not work before that. "Go to workspace" runs the handoff. A server
//     that predates the change sends no `pendingEmail` → the handoff runs at
//     once, as before.
//
//   • LOG IN (existing account): slot `LoginForm`. Submit calls
//     `el.sdk.login(identifier, password)` to authenticate the user's REAL
//     account, then `el.sdk.cloneDemoWorkspace({ demoOrgId })` to copy the
//     CURRENT demo session's workspace into a fresh real org owned by that
//     account, then hands the (now real-account) tokens off to my.symbols.app.
//
// Both paths end in `el.call('demoHandoff')` — the cross-origin token hop
// (functions/demoHandoff.js) that redirects to the shell's hash-token
// consumer (/auth/magic-callback on my.symbols.app) authed with the fresh
// tokens, so the user lands signed-in on their (converted or cloned)
// workspace. demo.symbols.app and my.symbols.app are different origins, so a
// plain redirect would drop the session — the handoff is what carries it.
//
// Mode toggle: `root._demoClaimMode` ('signup' | 'login', default 'signup')
// flips which form is slotted + which submit path runs. A link in the window
// switches between the two. The email field starts EMPTY in both modes — the
// demo account's email is a `demo-anon-…@demo.symbols.app` placeholder, never
// prefilled.
//
// CHROME REUSE:
//   - The BAR (`DemoClaimBar`, its own top-level component below) extends
//     the shared `TutorialNotification` card (UI-TUTORIAL-NOTIFICATION-
//     FAMILY-1) — same glassy canvas-card surface CanvasHelpCard gave it,
//     plus the family's shared TutorialNotificationButtons action row — and
//     overrides it for the banner look. It renders only on `/` (home) —
//     `hide` gates on `currentPath` — and the wrapper is `display: contents`
//     so it never participates in the shell-root grid.
//   - The MODAL body is `LoginWindow` (the /login window) slotted into the
//     brand `Modal`'s `Window` slot, with `SignupForm` / `LoginForm` slotted
//     into its `Flex` — REUSED verbatim, retargeted from login to claim/clone.
//
// Mounted ONCE at the shell root (workspace/app.js) as a sibling of AppHeader.
// Invisible to every real user: bar + Modal short-circuit on `!root.isDemo`.

// Draft + UI flags reset shape — cleared on close, on success, and on cancel
// so a reopen always starts blank.
const _resetClaim = {
  _demoClaimOpen: null,
  _demoClaimMode: 'signup',
  _demoClaimError: null
}

// Resolve the CURRENT demo session's active org id — the source workspace the
// clone path copies. Layered the same way functions/auth.js resolveActiveOrgId
// + the studio canvas notes read it: the resolved org on root, then the JWT
// pointer, then the SDK's ambient context, then the first org in the list.
const _demoOrgId = (root, sdk) =>
  root?.org?.id ||
  root?.jwtClaims?.workspace_org_id ||
  sdk?._context?.activeOrgId ||
  root?.orgs?.[0]?.id ||
  null

// DemoClaimBar — the visible pill's CONTENT card, a real top-level component
// (not a nested nameless child) so it can `extends: 'TutorialNotification'`
// while staying FA806-clean: audit_component flags any NESTED child key
// whose extends target differs from the key name, regardless of how
// meaningful the name is — the family's own established fix is a real,
// separately registered component (see RemoteUpdateBanner/InfraDemoBanner,
// both top-level exports with their own differently-named `extends`).
// `useCookie: false` neutralizes TutorialNotification's own cookie-dismiss
// gate (`if:`) — DemoClaimBanner's `hide:` below is this pill's ENTIRE
// visibility contract, it has no separate dismiss action, so it must not
// also disappear behind a tutorial-cookie nobody can set. Lottie/Hgroup
// nulled (unused, this is a single-line horizontal pill, not
// TutorialNotification's default vertical onboarding card) — own `Lead`
// kept verbatim, same idiom CreditBanner/InfraDemoBanner use. Named
// `DemoClaimBar`, not the generic `CanvasHelpCard` it used to extend
// (would collide at the DOM level with the real, separate CanvasHelpCard
// instances elsewhere — MEMORY project_two_elements_share_one_data_key) —
// this pill and CreditBanner's own pill can legitimately be on screen at
// the same time (a demo admin low on credits), so a shared generic name
// would recreate that exact collision one level up.
//
// NO `position`/inset props here — deliberately. `TutorialNotification`
// `extends: 'Navbar'` (company/packages/brand/components/Navbar.js), whose
// OWN base bakes in `position: 'sticky', top: '0'` as its identity (a
// navbar wants to stick to the page top). Overriding `position: 'fixed'`
// here does NOT reliably cancel Navbar's `top: '0'` — measured live: the
// card rendered as a near-fullscreen box (top:0 forced height to span the
// whole viewport instead of hugging content) even with an explicit
// `top: 'auto'` override attempted. `CanvasHelpCard`/`CanvasMessage` (what
// this used to extend) never hit this because CanvasMessage's own base has
// no Navbar in its chain at all. Fix: keep positioning OFF this card
// entirely and put it on `ClaimBarDock` below instead, a plain wrapper with
// no Navbar ancestry to fight.
export const DemoClaimBar = {
  extends: 'TutorialNotification',
  useCookie: false,
  flow: 'x',
  align: 'center',
  gap: 'A',
  padding: 'Z A',
  round: 'radiusControl',
  boxShadow: 'none',
  width: 'auto',
  // TutorialNotification's base caps the card at `maxWidth: 'H2'` (~646px)
  // for its vertical onboarding card. This is a single-line horizontal pill:
  // under that cap the lead sentence ellipsized at ~383px of 487px (measured
  // live, UI-TUTORIAL-NOTIFICATION-FAMILY-1). The viewport cap lives on
  // `ClaimBarDock` below, so the card itself is uncapped.
  maxWidth: 'none',
  Lottie: null,
  Hgroup: null,

  Lead: {
    flow: 'x',
    align: 'center',
    gap: 'Z',
    minWidth: '0',

    Icon: { name: 'clock', boxSize: 'A', color: 'caption' },

    Text: {
      tag: 'span',
      fontSize: 'Z2',
      lineHeight: '1.35',
      color: 'title',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      '@mobileL': { whiteSpace: 'normal' },
      text: '{{ workspace.demoClaim.banner | polyglot }}'
    }
  },

  TutorialNotificationButtons: {
    height: 'auto',
    childProps: {
      round: 'radiusPill',
      flex: '0 0 auto',
      minHeight: 'controlH',
      padding: '0 A',
      gap: 'X2',
      // CanvasButton/Button default to `aspect-ratio: 1/1` (an icon-only
      // square/circle button, the right default when every family sibling
      // provides an `icon`). This button has text and no icon — measured
      // live, without this override it renders 183×153px (near-square,
      // border-radius:999px rounds that into a circle) instead of a
      // 183×44px pill. Any text-only button in this family needs the same
      // override — see InfraDemoBanner/CreditWarningPill/RemoteUpdateBanner.
      aspectRatio: 'auto'
    },
    children: [
      {
        text: '{{ workspace.demoClaim.cta | polyglot }}',
        // Button law (design-direction.md §6/geex amendment): ONE
        // solid-ink primary capsule per screen, never a raw hue fill —
        // was `background:'blue', color:'white'` (fixed hue) before this
        // migration; InfraDemoBanner already established `theme:'primary'`
        // as the correct pattern for the same tier of CTA, adopted here
        // for real consistency, not just structural reuse.
        theme: 'primary',
        fontWeight: '700',
        onClick: (e, el, s) => {
          e.preventDefault()
          // Open in SIGN-UP mode with a blank draft. The anonymous demo
          // account's email is a `demo-anon-…@demo.symbols.app` placeholder
          // — never prefill it; the user types their REAL email (sign up)
          // or their existing account email (log in, after toggling).
          s.root.update({
            ..._resetClaim,
            _demoClaimOpen: true
          })
        }
      }
    ]
  }
}

export const DemoClaimBanner = {
  tag: 'div',
  // `display: contents` so this shell-root wrapper generates NO box of its own
  // and never becomes an in-flow grid item. Its only visible child (the
  // DemoClaimBar pill) and the claim Modal are both position:fixed, so
  // the wrapper has nothing to lay out. Without this the wrapper was a STATIC
  // grid child of the shell-root grid (every other shell overlay — ModalRoot,
  // DropdownRoot, TooltipRoot, AssistantDrawer, … — is position:fixed); on the
  // demo (isDemo true) it claimed the grid's 1fr content row and pushed the
  // router's `content` page-mount into a collapsed 0-height implicit row, which
  // rendered /tickets (and every non-home route) blank — the canvas was there
  // but ~60px below the viewport.
  display: 'contents',
  // Demo-only AND home-only (per Nika "only keep it on / page"): the claim bar
  // shows on `/` and is hidden on every other route. `subscribeTo` makes the
  // reactive `hide` re-run on SPA navigation (`currentPath`) and on the claim →
  // `isDemo:false` transition. `hide:` (not `if:`) keeps the node in the DOM so
  // the claim transition animates the bar out instead of destroying/recreating
  // it.
  subscribeTo: ['isDemo', 'currentPath'],
  hide: (el, s) => !s.root?.isDemo || shellStripWorkspacePrefix(s.root?.currentPath || '/') !== '/',

  // Plain positioning wrapper — NOT extending TutorialNotification/Navbar,
  // so it carries none of Navbar's baked-in `position:'sticky', top:'0'`
  // to fight. All the fixed/inset/z-index/responsive props that used to
  // live on DemoClaimBar itself now live here instead (see DemoClaimBar's
  // own comment above for why).
  ClaimBarDock: {
    position: 'fixed',
    right: 'auto',
    left: 'A',
    bottom: 'A',
    zIndex: '1500',
    // A bare `position:'fixed'` block defaults to `display:'block'` in this
    // setup — `display:'flex'` shrink-wraps this wrapper to DemoClaimBar's
    // own content width instead. (The pill's measured width, ~650px, is
    // correct as-is — that's the description sentence's real single-line
    // length at `whiteSpace:'nowrap'`, not a bug.)
    display: 'flex',
    width: 'auto',
    maxWidth: 'calc(100dvw - var(--spacing-B))',
    '@mobileL': {
      display: 'flex',
      left: 'Z',
      right: 'Z',
      bottom: 'Z',
      maxWidth: 'none'
    },
    // Bare-key auto-extend resolves DemoClaimBar (this file, above) — no
    // `extends` needed since the key name matches exactly.
    DemoClaimBar: {}
  },

  // Claim dialog — the brand Modal (backdrop, Escape, URL restore via the
  // stateKey contract) whose BODY is the REUSED /login window, supporting BOTH
  // sign-up (claim in place) and log-in (clone into the real account).
  //
  // The child key `Modal` auto-extends the brand Modal; `stateKey` makes this
  // instance driven by `_demoClaimOpen`.
  Modal: {
    stateKey: '_demoClaimOpen',
    // Escape/backdrop close resets the draft (shared Modal merges extraClose
    // into its close update).
    extraClose: {
      _demoClaimMode: 'signup',
      _demoClaimError: null
    },

    // Replace the brand Modal's default ModalWindow body with the /login
    // LoginWindow — same backdrop/stopPropagation wiring stays on this `Window`
    // key (Modal puts it there), the body chrome comes from LoginWindow.
    // Slot key MUST stay `Window` — the brand Modal places its backdrop /
    // stopPropagation wiring on the child literally keyed `Window` (renaming to
    // `LoginWindow` would break that slot resolution). The body chrome comes
    // from `extends: 'LoginWindow'`.
    Window: {
      extends: 'LoginWindow',
      // LoginWindow owns its isolated state. Seed every field BOTH forms read
      // EMPTY (static literal — SSR-safe, no param factory) so the demo
      // placeholder email never prefills; the user types their real address.
      // SignupForm mirrors into email/name/password, LoginForm into
      // identifier/password.
      state: {
        loading: false,
        email: '',
        name: '',
        identifier: '',
        password: ''
      },
      width: 'H1',
      maxWidth: '92vw',
      minHeight: 'auto',

      // Retarget the header copy per mode (sign up vs log in).
      ModalHeader: {
        Hgroup: {
          H: {
            text: (el, s) =>
              s.root?._demoClaimPendingEmail
                ? el.call('polyglot', 'workspace.demoClaim.pending.title')
                : s.root?._demoClaimMode === 'login'
                  ? el.call('polyglot', 'workspace.demoClaim.titleLogIn')
                  : el.call('polyglot', 'workspace.demoClaim.titleSignUp')
          },
          P: {
            tag: 'p',
            // Pending: the bundle sentence carries the address as {email};
            // `_demoClaimPendingSent === false` means the server could not
            // mail the link, and the sentence says so instead of "we sent".
            // `_demoClaimPendingSent` is read only behind the pending branch
            // (FA515): declare both keys so the sentence re-fires on either.
            textDeps: [
              (el, s) => s.root && s.root._demoClaimPendingEmail,
              (el, s) => s.root && s.root._demoClaimPendingSent
            ],
            text: (el, s) =>
              s.root?._demoClaimPendingEmail
                ? el
                    .call(
                      'polyglot',
                      s.root._demoClaimPendingSent === false
                        ? 'workspace.demoClaim.pending.notSent'
                        : 'workspace.demoClaim.pending.body'
                    )
                    .replace('{email}', s.root._demoClaimPendingEmail)
                : s.root?._demoClaimMode === 'login'
                  ? el.call('polyglot', 'workspace.demoClaim.ledeLogIn')
                  : el.call('polyglot', 'workspace.demoClaim.ledeSignUp')
          }
        }
      },

      Flex: {
        // OAuth has no role in claiming/cloning a demo workspace — drop the
        // Google/GitHub column the /login window ships.
        RequestAccessButtons: null,

        // SIGN-UP form — visible in signup mode. Reuses the /signup SignupForm
        // (Email + Name + Password + CheckButton); we retarget the submit
        // button label + add an inline error note + a mode-toggle link below.
        SignupForm: {
          hide: (el, s) => s.root?._demoClaimMode === 'login' || !!s.root?._demoClaimPendingEmail,

          Email: {
            Title: { text: '{{ workspace.demoClaim.email | polyglot }}' },
            Input: {
              placeholder: (el) => el.call('polyglot', 'workspace.demoClaim.emailPlaceholder')
            }
          },
          Password: {
            Input: {
              autocomplete: 'new-password',
              placeholder: (el) => el.call('polyglot', 'workspace.demoClaim.createPassword')
            }
          },

          ModalFooter: {
            // Inline error note — surfaces a failed claim in the window.
            ClaimError: {
              tag: 'p',
              hide: (el, s) => !s.root?._demoClaimError,
              role: 'alert',
              order: '-1',
              margin: '0',
              width: '100%',
              fontSize: 'Y',
              lineHeight: '1.4',
              // Raw brand red as INK measured 2.45:1 (DESIGN_LANGUAGE §8.5 rule
              // 2); `dangerInk` is the scheme pair that clears the floor.
              color: 'dangerInk',
              // `_demoClaimError` carries a bundle KEY for our own sentences and
              // the server's words for its own; polyglot echoes what is no key.
              text: (el, s) =>
                s.root?._demoClaimError ? el.call('polyglot', s.root._demoClaimError) : ''
            },
            CheckButton: {
              text: (el, s) =>
                el.call(
                  'polyglot',
                  s.loading ? 'workspace.demoClaim.signingUp' : 'workspace.demoClaim.signUp'
                ),
              disabled: (el, s) => !!s.loading || (s.password || '').length < 8
            },
            // Toggle to LOG IN.
            ModeToggle: {
              tag: 'button',
              type: 'button',
              order: '1',
              margin: '- - - auto',
              padding: '0',
              background: 'transparent',
              borderWidth: '0',
              cursor: 'pointer',
              fontSize: 'Z2',
              fontWeight: '400',
              color: 'caption',
              ':hover': { color: 'title' },
              text: '{{ workspace.demoClaim.haveAccount | polyglot }}',
              onClick: (e, el, s) => {
                e.preventDefault()
                s.root.update({ _demoClaimMode: 'login', _demoClaimError: null })
              }
            }
          }
        },

        // PENDING panel — after a claim whose new email awaits verification.
        // The header above carries the title + "we sent a link to {email}";
        // this panel adds the sign-in warning and the way forward.
        ClaimPending: {
          hide: (el, s) => !s.root?._demoClaimPendingEmail,
          flow: 'y',
          gap: 'A',
          width: '100%',
          SignInNote: {
            tag: 'p',
            role: 'note',
            margin: '0',
            fontSize: 'Z2',
            lineHeight: '1.4',
            color: 'caption',
            text: '{{ workspace.demoClaim.pending.signInNote | polyglot }}'
          },
          ClaimError: {
            tag: 'p',
            hide: (el, s) => !s.root?._demoClaimError,
            role: 'alert',
            margin: '0',
            width: '100%',
            fontSize: 'Y',
            lineHeight: '1.4',
            color: 'dangerInk',
            textDeps: [(el, s) => s.root && s.root._demoClaimError],
            text: (el, s) =>
              s.root?._demoClaimError ? el.call('polyglot', s.root._demoClaimError) : ''
          },
          Button: {
            type: 'button',
            theme: 'primary',
            width: '100%',
            minHeight: 'controlH',
            round: 'radiusPill',
            fontWeight: '700',
            text: '{{ workspace.demoClaim.pending.continue | polyglot }}',
            disabled: (el, s) => !!s.loading,
            onClick: async (e, el, s) => {
              e.preventDefault()
              s.root.update({ _demoClaimError: null })
              s.update({ loading: true })
              const hopped = await el.call('demoHandoff')
              if (!hopped) {
                s.update({ loading: false })
                s.root.update({ _demoClaimError: 'workspace.demoClaim.error.signUpHandoff' })
              }
            }
          }
        },

        // LOG-IN form — visible in login mode. Reuses the /login LoginForm
        // (Identifier + Password + CheckButton); we retarget the submit label
        // + add the same inline error + a mode-toggle link back to sign up.
        LoginForm: {
          hide: (el, s) => s.root?._demoClaimMode !== 'login' || !!s.root?._demoClaimPendingEmail,

          Flex: {
            // This reuses the SAME shared LoginForm as the /login page's
            // LoginWindow, which now carries fixed `data-testid` strings for
            // the Playwright smoke harness (qa-automation/pages/signin.page.ts).
            // Left inherited, this always-mounted-but-hidden claim modal would
            // duplicate every one of those testids on every page (DemoClaimBanner
            // is mounted unconditionally at the shell root — see its own
            // `hide`-not-`if` note above — and Modal keeps its content in the DOM
            // even while closed), so `getByTestId` would hit Playwright's strict-
            // mode "resolved to 2 elements" error for every real (non-demo) user
            // too. Null the testid on this instance so the /login-page one stays
            // the sole match.
            Identifier: {
              Title: { text: '{{ workspace.demoClaim.email | polyglot }}' },
              Input: {
                type: 'email',
                autocomplete: 'email',
                placeholder: (el) => el.call('polyglot', 'workspace.demoClaim.emailPlaceholder'),
                'data-testid': null
              }
            },
            Password: {
              Input: {
                autocomplete: 'current-password',
                placeholder: (el) => el.call('polyglot', 'workspace.demoClaim.yourPassword'),
                'data-testid': null
              }
            }
          },

          ModalFooter: {
            // Drop the /login "Forgot your password?" deep-link — it routes off
            // to /reset-password which has no place in this claim flow.
            DocsLink: null,
            ClaimError: {
              tag: 'p',
              hide: (el, s) => !s.root?._demoClaimError,
              role: 'alert',
              order: '-1',
              margin: '0',
              width: '100%',
              fontSize: 'Y',
              lineHeight: '1.4',
              // Raw brand red as INK measured 2.45:1 (DESIGN_LANGUAGE §8.5 rule
              // 2); `dangerInk` is the scheme pair that clears the floor.
              color: 'dangerInk',
              // `_demoClaimError` carries a bundle KEY for our own sentences and
              // the server's words for its own; polyglot echoes what is no key.
              text: (el, s) =>
                s.root?._demoClaimError ? el.call('polyglot', s.root._demoClaimError) : ''
            },
            CheckButton: {
              text: (el, s) =>
                el.call(
                  'polyglot',
                  s.loading ? 'workspace.demoClaim.loggingIn' : 'workspace.demoClaim.logIn'
                ),
              disabled: (el, s) => !!s.loading,
              // See the Flex.Identifier/Password note above — same testid
              // collision, same fix.
              'data-testid': null
            },
            // Toggle back to SIGN UP.
            ModeToggle: {
              tag: 'button',
              type: 'button',
              margin: '- - - auto',
              padding: '0',
              background: 'transparent',
              borderWidth: '0',
              cursor: 'pointer',
              fontSize: 'Z2',
              fontWeight: '400',
              color: 'caption',
              ':hover': { color: 'title' },
              text: '{{ workspace.demoClaim.newHere | polyglot }}',
              onClick: (e, el, s) => {
                e.preventDefault()
                s.root.update({ _demoClaimMode: 'signup', _demoClaimError: null })
              }
            }
          }
        }
      },

      // Submit — LoginWindow ships no onSubmit (the /login page adds one); this
      // is the claim/clone equivalent. Branches on the active mode. Reads the
      // (mirrored) field state — never a DOM read — so Chrome's password manager
      // clearing type=password at submit time can't strand the value.
      onSubmit: async (ev, el, s) => {
        ev.preventDefault()
        const root = s.root
        const mode = root?._demoClaimMode === 'login' ? 'login' : 'signup'

        if (mode === 'signup') {
          // SIGN UP → convert the demo account in place, keep the workspace.
          const email = (s.email || '').trim().toLowerCase()
          const password = s.password || ''
          const name = (s.name || '').trim()
          if (!email) {
            root.update({ _demoClaimError: 'workspace.demoClaim.error.emailRequired' })
            return
          }
          if (password.length < 8) {
            root.update({ _demoClaimError: 'workspace.demoClaim.error.passwordShort' })
            return
          }
          s.update({ loading: true })
          root.update({ _demoClaimError: null })
          try {
            // SDK owns transport — claimDemoAccount POSTs /demo/claim; the server
            // sets the password + real email + clears isDemo on the user and org.
            const claimed = await el.sdk.claimDemoAccount({ email, password, name })
            // New email parked on pendingEmail (server B-5): say so before
            // leaving — the user must click the verification link, and sign-in
            // with the new email fails until then. The pending panel's
            // "Go to workspace" runs the handoff.
            if (claimed && claimed.pendingEmail) {
              s.update({ loading: false })
              root.update({
                _demoClaimPendingEmail: claimed.pendingEmail,
                _demoClaimPendingSent: claimed.emailVerificationSent !== false
              })
              return
            }
            // Hand the (still-valid) tokens off to my.symbols.app authed. Leaves
            // loading true — the page is navigating away.
            const hopped = await el.call('demoHandoff')
            if (!hopped) {
              s.update({ loading: false })
              root.update({
                _demoClaimError: 'workspace.demoClaim.error.signUpHandoff'
              })
            }
          } catch (err) {
            console.warn('[DemoClaimBanner] sign-up failed', err)
            s.update({ loading: false })
            root.update({
              _demoClaimError: (err && err.message) || 'workspace.demoClaim.error.signUpFailed'
            })
          }
          return
        }

        // LOG IN → authenticate the real account, clone the demo workspace into
        // it, then hand off.
        const identifier = (s.identifier || '').trim().toLowerCase()
        const password = s.password || ''
        if (!identifier) {
          root.update({ _demoClaimError: 'workspace.demoClaim.error.emailRequired' })
          return
        }
        if (!password) {
          root.update({ _demoClaimError: 'workspace.demoClaim.error.passwordRequired' })
          return
        }
        // Capture the CURRENT demo session's active org BEFORE login swaps the
        // session out — that's the workspace we clone into the real account.
        const demoOrgId = _demoOrgId(root, el.sdk)
        if (!demoOrgId) {
          root.update({
            _demoClaimError: 'workspace.demoClaim.error.noDemoWorkspace'
          })
          return
        }
        s.update({ loading: true })
        root.update({ _demoClaimError: null })
        try {
          // Authenticate the real account — the SDK TokenManager swaps the demo
          // tokens for the real-account tokens on success.
          await el.sdk.login(identifier, password)
          // A sign-in on this origin: its refresh token is its own (review D1).
          el.call('markOwnSession')
          // Copy the demo workspace into a fresh real org owned by this account.
          await el.sdk.cloneDemoWorkspace({ demoOrgId })
          // Hand the real-account tokens off to my.symbols.app authed.
          const hopped = await el.call('demoHandoff')
          if (!hopped) {
            s.update({ loading: false })
            root.update({
              _demoClaimError: 'workspace.demoClaim.error.logInHandoff'
            })
          }
        } catch (err) {
          console.warn('[DemoClaimBanner] log-in + clone failed', err)
          s.update({ loading: false })
          const msg = (err && (err.message || err.cause?.message)) || ''
          const isCredential =
            err?.cause?.status === 401 ||
            /password is incorrect/i.test(msg) ||
            /invalid credentials/i.test(msg) ||
            /user not found/i.test(msg)
          root.update({
            _demoClaimError: isCredential
              ? 'workspace.demoClaim.error.badCredentials'
              : msg || 'workspace.demoClaim.error.logInFailed'
          })
        }
      }
    }
  }
}
